import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Copy, Trash2, Download, Mic, Type } from 'lucide-react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Button from '../Common/Button';
import ConfirmModal from '../Common/ConfirmModal';
import { formatTimestamp } from '../../utils/timezoneUtils';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/apiService';

const DrawerOverlay = styled(motion.div)`
  position: fixed;
  top: 32px; /* Leave space for title bar (close/minimize/restore buttons) */
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
`;

const DrawerContainer = styled(motion.div)`
  width: 600px;
  max-width: 90vw;
  height: calc(100vh - 32px); /* Account for title bar */
  background: ${({ theme }) => theme.colors.dark.surface};
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
  background: ${({ theme }) => theme.colors.dark.elevated};
`;

const DrawerTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const DrawerActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.xl};
  
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.dark.bg};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.dark.border};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
  }
`;

const ConversationMeta = styled.div`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
  }
`;

const MetaLabel = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-weight: 500;
`;

const MetaValue = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.text};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ModeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme, mode }) => 
    mode === 'voice' ? `${theme.colors.accent.purple}20` : `${theme.colors.primary[500]}20`};
  color: ${({ theme, mode }) => 
    mode === 'voice' ? theme.colors.accent.purple : theme.colors.primary[400]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 500;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const MessageBubble = styled.div`
  background: ${({ theme, isUser }) => 
    isUser ? theme.colors.dark.elevated : theme.colors.dark.elevated};
  border: 1px solid ${({ theme, isUser }) => 
    isUser ? theme.colors.dark.border : theme.colors.dark.border};
  border-left: ${({ theme, isUser }) => 
    isUser ? `3px solid ${theme.colors.primary[500]}` : `3px solid ${theme.colors.accent.purple}`};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  position: relative;
`;

const MessageAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme, isUser }) => 
    isUser ? theme.colors.primary[400] : theme.colors.accent.purple};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const MessageText = styled.div`
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
  line-height: 1.6;
  word-wrap: break-word;
  
  /* Full markdown styling for drawer */
  p {
    margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary[400]};
  }
  
  em {
    font-style: italic;
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
  
  code {
    background: ${({ theme }) => theme.colors.primary[500]}15;
    color: ${({ theme }) => theme.colors.primary[400]};
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.875rem;
    font-family: 'Courier New', Consolas, Monaco, monospace;
  }
  
  pre {
    background: ${({ theme }) => theme.colors.dark.elevated};
    border: 1px solid ${({ theme }) => theme.colors.dark.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing.md};
    margin: ${({ theme }) => theme.spacing.sm} 0;
    overflow-x: auto;
    
    code {
      background: none;
      padding: 0;
      color: ${({ theme }) => theme.colors.dark.text};
      font-size: 0.875rem;
    }
  }
  
  ul, ol {
    margin: ${({ theme }) => theme.spacing.sm} 0;
    padding-left: ${({ theme }) => theme.spacing.xl};
    
    li {
      margin: ${({ theme }) => theme.spacing.xs} 0;
    }
  }
  
  blockquote {
    border-left: 3px solid ${({ theme }) => theme.colors.primary[500]};
    padding-left: ${({ theme }) => theme.spacing.md};
    margin: ${({ theme }) => theme.spacing.sm} 0;
    color: ${({ theme }) => theme.colors.dark.textSecondary};
    font-style: italic;
  }
  
  h1, h2, h3, h4, h5, h6 {
    color: ${({ theme }) => theme.colors.dark.text};
    font-weight: 600;
    margin: ${({ theme }) => theme.spacing.md} 0 ${({ theme }) => theme.spacing.sm} 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  a {
    color: ${({ theme }) => theme.colors.primary[400]};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  // Table styling for markdown tables
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
    background: ${({ theme }) => theme.colors.dark.bg};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    overflow: hidden;
  }
  
  thead {
    background: ${({ theme }) => theme.colors.dark.border};
  }
  
  th {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    text-align: left;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary[400]};
    border-bottom: 2px solid ${({ theme }) => theme.colors.primary[500]};
  }
  
  td {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
    color: ${({ theme }) => theme.colors.dark.text};
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  tr:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
  }
  
  // Alternating row colors for better readability
  tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const MessageTime = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.sm};
  text-align: left;
`;

const DrawerFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.xl};
  border-top: 1px solid ${({ theme }) => theme.colors.dark.border};
  background: ${({ theme }) => theme.colors.dark.elevated};
`;

const ConversationDrawer = ({ conversation, onClose, onDelete }) => {
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleCopy = () => {
    const text = `User: ${conversation.user.text}\n\nSARVIK: ${conversation.assistant?.text || '(No response)'}`;
    navigator.clipboard.writeText(text);
    // TODO: Show success toast
    console.log('Copied to clipboard');
  };

  const handleExport = () => {
    const exportDate = conversation.user.timestamp_local 
      ? new Date(conversation.user.timestamp_local).toLocaleString('en-US', { 
          year: 'numeric', month: 'short', day: 'numeric', 
          hour: '2-digit', minute: '2-digit', hour12: true 
        })
      : formatTimestamp(conversation.user.timestamp, user?.timezone || 'UTC');
    
    const text = `Conversation Export
Date: ${exportDate}
Mode: ${conversation.user.mode}

User (${user?.name || 'You'}):
${conversation.user.text}

SARVIK:
${conversation.assistant?.text || '(No response)'}
`;
    
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${conversation.user.id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await apiService.deleteConversation(conversation.user.id, token);
      console.log('✅ Conversation deleted successfully');
      
      // Call onDelete callback if provided (to refresh the list)
      if (onDelete) {
        onDelete(conversation.user.id);
      }
      
      // Close modal and drawer
      setShowDeleteModal(false);
      onClose();
    } catch (error) {
      console.error('❌ Error deleting conversation:', error);
      toast.error('Failed to delete conversation. Please try again.', {
        duration: 5000,
      });
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <DrawerOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <DrawerContainer
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <DrawerHeader>
          <DrawerTitle>Full Conversation</DrawerTitle>
          <DrawerActions>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X />
            </Button>
          </DrawerActions>
        </DrawerHeader>

        <DrawerContent>
          <ConversationMeta>
            <MetaRow>
              <MetaLabel>Participant</MetaLabel>
              <MetaValue> {user?.name || 'You'}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>Date & Time</MetaLabel>
              <MetaValue>
                {conversation.user.timestamp_local 
                  ? new Date(conversation.user.timestamp_local).toLocaleString('en-US', { 
                      year: 'numeric', month: 'short', day: 'numeric', 
                      hour: '2-digit', minute: '2-digit', hour12: true 
                    })
                  : formatTimestamp(conversation.user.timestamp, user?.timezone || 'UTC', { format: 'full' })
                }
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>Mode</MetaLabel>
              <MetaValue>
                <ModeIndicator mode={conversation.user.mode}>
                  {conversation.user.mode === 'voice' ? <Mic /> : <Type />}
                  {conversation.user.mode}
                </ModeIndicator>
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>Conversation ID</MetaLabel>
              <MetaValue style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                {conversation.user.id}
              </MetaValue>
            </MetaRow>
          </ConversationMeta>

          <MessagesContainer>
            {/* User Message */}
            <MessageBubble isUser={true}>
              <MessageAuthor isUser={true}>
                 {user?.name || 'You'}
              </MessageAuthor>
              <MessageText isUser={true}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{conversation.user.text}</ReactMarkdown>
              </MessageText>
            </MessageBubble>

            {/* Assistant Response */}
            {conversation.assistant && (
              <MessageBubble isUser={false}>
                <MessageAuthor isUser={false}>
                   SARVIK
                </MessageAuthor>
                <MessageText isUser={false}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{conversation.assistant.text}</ReactMarkdown>
                </MessageText>
              </MessageBubble>
            )}
          </MessagesContainer>
        </DrawerContent>

        <DrawerFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            style={{ flex: 1 }}
          >
            <Copy />
            Copy
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExport}
            style={{ flex: 1 }}
          >
            <Download />
            Export
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            style={{ flex: 1 }}
          >
            <Trash2 />
            Delete
          </Button>
        </DrawerFooter>
      </DrawerContainer>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Conversation"
        subtitle="This action cannot be undone"
        message="Are you sure you want to delete this conversation? Both the user message and assistant response will be permanently removed from your history."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        icon={Trash2}
        loading={isDeleting}
        loadingText="Deleting..."
      />
    </DrawerOverlay>
  );
};

export default ConversationDrawer;
